// File: client/src/platform/hostPersistence.js
//
// Generic, variant-agnostic snapshot for in-progress LOCAL / AI games. The Variant
// Host stores { variantId, config, gameState, moveHistory, payload } where `payload`
// is the variant's OWN opaque state (the host never inspects it). One in-progress
// game is kept, keyed by variantId, so resuming only offers a snapshot for the same
// variant + opponent.
//
// This supersedes the UNO-specific variant/persistence.js for platform use; UNO's
// state now travels inside `payload` and is (de)serialised by the UNO runtime.

const STORAGE_KEY = 'chess_variant_snapshot';
const VERSION = 1;

function getStore() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (_) { /* privacy mode */ }
  return null;
}

/**
 * @param {object} snap { variantId, config, gameState, moveHistory, payload }
 */
export function saveHostSnapshot(snap) {
  const store = getStore();
  if (!store || !snap || !snap.variantId) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ version: VERSION, savedAt: Date.now(), snap }));
  } catch (_) { /* quota */ }
}

/**
 * Load the snapshot if it matches the given variantId (and opponent, if provided).
 * Returns the inner snap, or null.
 * @param {string} variantId
 * @param {object} [config] { opponent }
 */
export function loadHostSnapshot(variantId, config) {
  const store = getStore();
  if (!store) return null;

  let raw;
  try { raw = store.getItem(STORAGE_KEY); } catch (_) { return null; }
  if (!raw) return null;

  let parsed;
  try { parsed = JSON.parse(raw); } catch (_) { clearHostSnapshot(); return null; }

  if (!parsed || parsed.version !== VERSION || !parsed.snap) { clearHostSnapshot(); return null; }
  const snap = parsed.snap;

  if (snap.variantId !== variantId) return null; // snapshot is for a different mode
  if (!snap.gameState || !Array.isArray(snap.gameState.board) || snap.gameState.board.length !== 8) {
    clearHostSnapshot();
    return null;
  }
  if (config && snap.config && config.opponent && snap.config.opponent !== config.opponent) {
    return null;
  }
  return snap;
}

/** Remove any stored snapshot. */
export function clearHostSnapshot() {
  const store = getStore();
  if (!store) return;
  try { store.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
}

export const __TEST__ = { STORAGE_KEY, VERSION };
